import React from "react";
import Container from "./Container";

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Store error info for display
    this.setState({ errorInfo });

    // In production, you might want to log to an error reporting service
    if (process.env.NODE_ENV === "production") {
      // Log to error reporting service (e.g., Sentry, LogRocket)
      // logErrorToService(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Container>
          <div className="min-h-[60vh] flex flex-col items-center justify-center text-center py-12">
            <div className="max-w-md">
              {/* Error Icon */}
              <div className="mb-6">
                <svg
                  className="w-20 h-20 mx-auto text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                Oops! Something went wrong
              </h1>

              <p className="text-gray-600 mb-6">
                We're sorry, but something unexpected happened. Please try again
                or return to the home page.
              </p>

              {/* Show error details in development */}
              {process.env.NODE_ENV !== "production" && this.state.error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-left">
                  <p className="text-sm font-medium text-red-800 mb-2">
                    Error Details:
                  </p>
                  <pre className="text-xs text-red-700 overflow-auto max-h-40">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleRetry}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Go to Home
                </button>
              </div>
            </div>
          </div>
        </Container>
      );
    }

    return this.props.children;
  }
}

/**
 * Functional wrapper for ErrorBoundary with reset capability
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {React.ReactNode} props.fallback - Custom fallback UI
 * @param {Function} props.onError - Callback when error occurs
 */
export const ErrorBoundaryWithReset = ({
  children,
  fallback,
  onError,
  resetKeys = [],
}) => {
  const [resetKey, setResetKey] = React.useState(0);

  // Reset the error boundary when any of the reset keys change
  React.useEffect(() => {
    setResetKey((prev) => prev + 1);
  }, [...resetKeys]);

  return (
    <ErrorBoundary key={resetKey} fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;
